import { useState } from 'react'
import { ApiError, api, fmtTime, transitionMission, type Me, type Mission } from '../api'
import { useT } from '../lang'
import { invalidate, useApi } from '../useApi'
import { Link, navigate } from '../router'
import { PageHead } from '../components/Shell'
import { Empty, ErrorBox, Field, Rows, StateBadge } from '../components/bits'
import { Menu, type MenuAction } from '../components/Menu'

interface Site {
  site_id: string
  name: string
  project_id: string | null
  operator: string | null
  lat: number | null
  lon: number | null
  mission_count: number
  active_missions: number
  active_mission_id: string | null
  /** Null for a bench or a harbour: the register knows nothing about it, so
   *  nothing keyed on a locality number applies. */
  locality_no: number | null
}

export function Sites({ me }: { me: Me }) {
  const t = useT()
  // Live sites first and alone by default. A site is never deleted -- it is
  // where a year of recordings came from -- but a list of places nobody is
  // listening to is noise when you are trying to find the one that matters.
  const [all, setAll] = useState(false)
  const q = useApi<{ items: Site[]; total: number }>('/v1/sites?limit=200')
  const every = q.data?.items ?? []
  const items = all ? every : every.filter((s) => s.active_missions > 0)
  const hidden = every.length - items.length

  return (
    <>
      <PageHead
        title={t('Places')}
      />
      <ErrorBox error={q.error} />

      <div className="card flush">
        {q.cold ? (
          <Rows />
        ) : items.length === 0 ? (
          <Empty title={every.length ? 'Nothing deployed anywhere' : 'No sites yet'}>
            {every.length
              ? 'Every site you have is currently without a hydrophone in the water.'
              : 'A site is created for you the first time you add a hydrophone somewhere new.'}
          </Empty>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Site</th>
                <th>{t('Position')}</th>
                <th className="r">Deployments</th>
                <th className="r">Now</th>
                <th className="r"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.site_id}>
                  <td data-l="Site">
                    <Link className="rowlink" to={`/sites/${encodeURIComponent(s.site_id)}`}>
                      {s.name}
                    </Link>
                    <div className="mono faint">{s.site_id}</div>
                  </td>
                  <td data-l="Position" className="num faint">
                    {s.lat != null && s.lon != null
                      ? `${s.lat.toFixed(4)}, ${s.lon.toFixed(4)}`
                      : '—'}
                  </td>
                  <td data-l="Deployments" className="r num">{s.mission_count}</td>
                  <td data-l="Now" className="r">
                    {s.active_missions > 0 ? (
                      <span className="badge on">listening</span>
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td data-l="" className="r menu-cell">
                    <SiteMenu s={s} canWrite={me.can_write}
                      onChanged={() => { invalidate('/v1/sites'); q.reload() }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(hidden > 0 || all) && (
        <button className="btn ghost sm" style={{ marginTop: 14 }} onClick={() => setAll(!all)}>
          {all
            ? 'Show only sites with a hydrophone deployed'
            : `Show ${hidden} past site${hidden === 1 ? '' : 's'}`}
        </button>
      )}
    </>
  )
}

/** A site is never retired and never deleted -- it is where a year of
 *  recordings came from, and this list already hides places with nothing in
 *  the water. So the actions here are about the deployment at the site, which
 *  is the thing that can actually end. */
function SiteMenu({
  s, canWrite, onChanged,
}: { s: Site; canWrite: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState(false)

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await fn()
      onChanged()
    } catch {
      /* The row is about to be re-fetched either way; a failed action leaves
         the list showing what the server still believes, which is correct. */
    } finally {
      setBusy(false)
    }
  }

  const actions: MenuAction[] = [
    { label: 'Open site', onSelect: () => navigate(`/sites/${encodeURIComponent(s.site_id)}`) },
  ]
  const mid = s.active_mission_id
  if (canWrite && mid) {
    actions.push({
      label: 'Open the deployment',
      onSelect: () => navigate(`/missions/${encodeURIComponent(mid)}`),
    })
    actions.push({
      label: 'Bring the hydrophone in',
      confirm: 'Bring it in — confirm',
      detail: 'Records that it came out of the water here. The deployment closes and keeps all its recordings.',
      onSelect: () => void run(() => transitionMission(mid, 'recovered')),
    })
    actions.push({
      label: 'This was only a test',
      confirm: 'Archive the deployment — confirm',
      detail: 'Closes it as archived rather than recovered, so it stops counting as a real deployment here.',
      onSelect: () => void run(() => transitionMission(mid, 'archived')),
    })
  }

  return <Menu actions={actions} busy={busy} label={`Actions for ${s.name}`} />
}

interface SiteDetail extends Site {
  /** What this farm has bought. Hides a link the API would
   *  refuse anyway; the refusal is still the boundary. */
  features?: string[]
  notes: string | null
  missions: Pick<Mission, 'mission_id' | 'compute_id' | 'microphone_id' | 'state' | 'started_at' | 'ended_at' | 'deploy_depth_m' | 'tier'>[]
}

export function SitePage({ id, me }: { id: string; me: Me }) {
  const path = `/v1/sites/${encodeURIComponent(id)}`
  const q = useApi<SiteDetail>(path)
  const s = q.data

  if (q.cold) return <Rows n={6} />
  if (q.error) return <ErrorBox error={q.error} />
  if (!s) return null

  const current = s.missions.find((m) => m.state === 'active')

  return (
    <>
      <PageHead
        title={s.name}
        lead={<span className="mono">{s.site_id}</span>}
        cta={
          <>
            {/* Only when the farm is a registered locality, because that is
                what the lice page is keyed on. Offering it on a bench site
                would open a page that can only say "not a locality". */}
            {s.locality_no != null && (
              <Link className="btn ghost" to={`/sites/${encodeURIComponent(s.site_id)}/lus`}>
                Lice
              </Link>
            )}
            {/* Staff always, because the customer product should not be
                something nobody at Phoniq can look at — the same argument
                /lytt already makes. For a customer the link follows the
                purchase. */}
            {(me.is_staff !== false || s.features?.includes('analysis.log')) && (
              <Link className="btn ghost" to={`/sites/${encodeURIComponent(s.site_id)}/logg`}>
                Log
              </Link>
            )}
            {current && (
              <Link className="btn primary" to={`/missions/${encodeURIComponent(current.mission_id)}`}>
                Open current deployment
              </Link>
            )}
          </>
        }
      />

      <div className="stack">
        <SiteDetails s={s} me={me} path={path}
                     onSaved={() => { invalidate(path); q.reload() }} />

        <div className="card flush">
          <div className="card-head">
            <h2>Deployment history</h2>
          </div>
          {/* The missions table IS the location history. There is deliberately
              no "assign a sensor to this site" action anywhere in this app:
              reassignment means ending one mission and starting another, and a
              UI that wrote an assignment row instead would break the one
              property the data model exists to guarantee. */}
          {s.missions.length === 0 ? (
            <Empty title="Never deployed" />
          ) : (
            <div className="scroll-x">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Mission</th>
                    <th>Node</th>
                    <th>Hydrophone</th>
                    <th>State</th>
                    <th>From</th>
                    <th>To</th>
                  </tr>
                </thead>
                <tbody>
                  {s.missions.map((m) => (
                    <tr key={m.mission_id}>
                      <td data-l="Mission">
                        <Link className="rowlink" to={`/missions/${encodeURIComponent(m.mission_id)}`}>
                          {m.mission_id}
                        </Link>
                      </td>
                      <td data-l="Node" className="mono faint">{m.compute_id ?? '—'}</td>
                      <td data-l="Hydrophone" className="mono faint">{m.microphone_id ?? '—'}</td>
                      <td data-l="State"><StateBadge state={m.state} /></td>
                      <td data-l="From" className="num faint">{fmtTime(m.started_at)}</td>
                      <td data-l="To" className="num faint">
                        {m.ended_at ? fmtTime(m.ended_at) : <span className="faint">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  )
}


/** Where this place is, who runs it, and a way to correct any of it.
 *
 *  None of these could be changed from the interface until now. The PATCH
 *  endpoint has always accepted name, operator, lat, lon and notes, and no
 *  screen offered one of them -- so a site named during commissioning kept that
 *  name for ever, including the typos, and a position entered before anyone
 *  had a GPS reading stayed wrong.
 *
 *  The name matters more than it looks. It is the heading on every fleet card,
 *  so it is the word an operator actually navigates by; "Bench - living room,
 *  Bergen" is a working name that will want to become a farm name, and there
 *  was no way to make that happen short of SQL.
 */
function SiteDetails({
  s, me, path, onSaved,
}: {
  s: { name: string; lat: number | null; lon: number | null; operator: string | null; notes: string | null }
  me: Me
  path: string
  onSaved: () => void
}) {
  const t = useT()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const [lat, setLat] = useState('')
  const [lon, setLon] = useState('')
  const [operator, setOperator] = useState('')
  const [notes, setNotes] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const start = () => {
    setName(s.name ?? '')
    setLat(s.lat?.toString() ?? '')
    setLon(s.lon?.toString() ?? '')
    setOperator(s.operator ?? '')
    setNotes(s.notes ?? '')
    setErr(null)
    setEditing(true)
  }

  const save = async () => {
    if (!name.trim()) { setErr('A site needs a name -- it is the heading on every card.'); return }
    setBusy(true)
    setErr(null)
    try {
      // Empty clears, rather than being skipped. A position entered by mistake
      // has to be removable, and "unknown" is a truthful state where a stale
      // coordinate is not.
      await api.patch(path, {
        name: name.trim(),
        operator: operator.trim() || null,
        lat: lat.trim() === '' ? null : Number(lat),
        lon: lon.trim() === '' ? null : Number(lon),
        notes: notes.trim() || null,
      })
      setEditing(false)
      onSaved()
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail ?? e.code : 'Save failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 14 }}>
        <h2 className="display" style={{ fontSize: 17 }}>{t('Details')}</h2>
      </div>

      <dl className="dl">
        <dt>{t('Name')}</dt>
        <dd>{s.name}</dd>
        <dt>{t('Position')}</dt>
        <dd className="num">
          {s.lat != null && s.lon != null
            ? `${s.lat}, ${s.lon}`
            : <span className="faint">unknown</span>}
        </dd>
        <dt>{t('Operator')}</dt>
        <dd>{s.operator ?? <span className="faint">—</span>}</dd>
        <dt>{t('Notes')}</dt>
        <dd>{s.notes || <span className="faint">—</span>}</dd>
      </dl>

      {me.can_write && !editing && (
        <button className="btn sm" style={{ marginTop: 16 }} onClick={start}>
          Edit site
        </button>
      )}

      {editing && (
        <div className="stack" style={{ marginTop: 16, gap: 14 }}>
          <Field label="Name" hint="The heading on every fleet card. What you would say out loud to describe where this is.">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Bench — living room, Bergen" />
          </Field>
          <div className="grid c2">
            <Field label="Latitude" hint="Decimal degrees, north positive.">
              <input type="number" step="0.00001" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="60.39299" />
            </Field>
            <Field label="Longitude" hint="Decimal degrees, east positive.">
              <input type="number" step="0.00001" value={lon} onChange={(e) => setLon(e.target.value)} placeholder="5.32415" />
            </Field>
          </div>
          <Field label="Operator" hint="Who runs the site. The farm company, or your own name for a bench.">
            <input value={operator} onChange={(e) => setOperator(e.target.value)} />
          </Field>
          <Field label="Notes">
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Depth, moorings, access, anything the next person needs" />
          </Field>
          {err && <div className="notice bad">{err}</div>}
          <div className="row">
            <button className="btn sm" disabled={busy} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button className="btn ghost sm" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}
